import { useCallback, useEffect, useRef, useState } from "react";

export const MAX_DESKTOP_COLUMNS = 6;

const COLUMNS_FALLBACK = 1;
const SELECTOR_LIST_GAP_PX = 8;
const SELECTOR_ATOM_MIN_WIDTH_PX = 48;
const SELECTOR_ATOM_HORIZONTAL_CHROME_PX = 28;
const ESTIMATED_DESKTOP_CONTAINER_WIDTH_PX = 400;
const ESTIMATED_CHARACTER_WIDTH_PX = 8;

export type SelectorListColumnItem = {
  name: string;
  offerLabel?: string;
};

export function getSelectorListColumns(
  containerWidth: number,
  widestLabelWidth: number,
) {
  if (containerWidth <= 0 || widestLabelWidth <= 0) {
    return COLUMNS_FALLBACK;
  }

  const itemWidth = Math.max(
    SELECTOR_ATOM_MIN_WIDTH_PX,
    widestLabelWidth + SELECTOR_ATOM_HORIZONTAL_CHROME_PX,
  );
  const columns = Math.floor(
    (containerWidth + SELECTOR_LIST_GAP_PX) /
      (itemWidth + SELECTOR_LIST_GAP_PX),
  );

  return Math.max(COLUMNS_FALLBACK, columns);
}

export function getSelectorListDesktopColumns(
  containerWidth: number,
  widestLabelWidth: number,
) {
  const columns = getSelectorListColumns(containerWidth, widestLabelWidth);
  return Math.min(MAX_DESKTOP_COLUMNS, columns);
}

export function estimateDesktopColumns(items: SelectorListColumnItem[]) {
  const widestLabelWidth = items.reduce(
    (widest, { name, offerLabel }) =>
      Math.max(
        widest,
        Math.max(name.length, offerLabel?.length ?? 0) *
          ESTIMATED_CHARACTER_WIDTH_PX,
      ),
    0,
  );

  return getSelectorListDesktopColumns(
    ESTIMATED_DESKTOP_CONTAINER_WIDTH_PX,
    widestLabelWidth,
  );
}

export function useSelectorListColumns(items: SelectorListColumnItem[]) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [initialColumns] = useState(() => ({
    desktop: estimateDesktopColumns(items),
    mobile: COLUMNS_FALLBACK,
  }));
  const columnsRef = useRef(initialColumns);
  const measurementRef = useRef<HTMLDivElement | null>(null);
  const [desktopColumns, setDesktopColumns] = useState(initialColumns.desktop);
  const [mobileColumns, setMobileColumns] = useState(initialColumns.mobile);

  const updateColumns = useCallback((desktop: number, mobile: number) => {
    if (
      columnsRef.current.desktop === desktop &&
      columnsRef.current.mobile === mobile
    ) {
      return;
    }

    columnsRef.current = { desktop, mobile };
    setDesktopColumns(desktop);
    setMobileColumns(mobile);
  }, []);

  const calculateColumns = useCallback(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;

    if (!container || !measurement) {
      return;
    }

    const containerWidth = container.getBoundingClientRect().width;
    const widestLabelWidth = Array.from(measurement.children).reduce(
      (widest, child) => Math.max(widest, child.getBoundingClientRect().width),
      0,
    );
    const baseColumns = getSelectorListColumns(
      containerWidth,
      widestLabelWidth,
    );
    const desktopCols = Math.min(MAX_DESKTOP_COLUMNS, baseColumns);
    const mobileCols = Math.min(MAX_DESKTOP_COLUMNS, baseColumns);

    updateColumns(desktopCols, mobileCols);
  }, [updateColumns]);

  useEffect(() => {
    calculateColumns();
  }, [calculateColumns, items]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", calculateColumns);
      return () => window.removeEventListener("resize", calculateColumns);
    }

    const observer = new ResizeObserver(calculateColumns);
    observer.observe(container);

    return () => observer.disconnect();
  }, [calculateColumns]);

  return {
    containerRef,
    desktopColumns,
    mobileColumns,
    measurementRef,
  };
}
